import React, { createContext, useContext, useState, ReactNode } from 'react';
import { GameMode } from '../types/collection';
import { getCollectionByMode } from '../constants/gameModes';
import { Rating } from '../constants/rating';

interface GameSettingsContextType {
  gameMode: GameMode;
  setGameMode: (mode: GameMode) => void;
  rating: Rating;
  setRating: (rating: Rating) => void;
  artists: string[];
  setArtists: (artists: string[]) => void;
  rounds: number;
  setRounds: (rounds: number) => void;
  showFavorites: boolean;
  setShowFavorites: (show: boolean) => void;
  difficulty: number | null;
  setDifficulty: (difficulty: number | null) => void;
  isBlurry: boolean;
  setIsBlurry: (isBlurry: boolean) => void;
  blurAmount: number;
  setBlurAmount: (amount: number) => void;
  hasTimeLimit: boolean;
  setHasTimeLimit: (hasLimit: boolean) => void;
  timeLimitMs: number;
  setTimeLimitMs: (ms: number) => void;
  isCropped: boolean;
  setIsCropped: (isCropped: boolean) => void;
  cropPercentage: number;
  setCropPercentage: (percentage: number) => void;
  isFlipped: boolean;
  setIsFlipped: (isFlipped: boolean) => void;
  isInverted: boolean;
  setIsInverted: (isInverted: boolean) => void;
  isMosaic: boolean;
  setIsMosaic: (isMosaic: boolean) => void;
  isNoColor: boolean;
  setIsNoColor: (isNoColor: boolean) => void;
  isRanked: boolean;
  setIsRanked: (isRanked: boolean) => void;
  artistRangeStart: number;
  setArtistRangeStart: (start: number) => void;
  artistRangeEnd: number;
  setArtistRangeEnd: (end: number) => void;
}

const GameSettingsContext = createContext<GameSettingsContextType | undefined>(undefined);

export const GameSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [gameMode, setGameMode] = useState<GameMode>('artist');
  const [rating, setRating] = useState<Rating>('all');
  const currentCollection = getCollectionByMode(gameMode);
  
  const [artists, setArtists] = useState<string[]>(currentCollection.map(item => item.name));
  const [rounds, setRounds] = useState(5);
  const [showFavorites, setShowFavorites] = useState(false);
  const [difficulty, setDifficulty] = useState<number | null>(null);
  const [isBlurry, setIsBlurry] = useState(false);
  const [blurAmount, setBlurAmount] = useState(10);
  const [hasTimeLimit, setHasTimeLimit] = useState(false);
  const [timeLimitMs, setTimeLimitMs] = useState(100);
  const [isCropped, setIsCropped] = useState(false);
  const [cropPercentage, setCropPercentage] = useState(10);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isInverted, setIsInverted] = useState(false);
  const [isMosaic, setIsMosaic] = useState(false);
  const [isNoColor, setIsNoColor] = useState(false);
  const [isRanked, setIsRanked] = useState(false);
  const [artistRangeStart, setArtistRangeStart] = useState(1);
  const [artistRangeEnd, setArtistRangeEnd] = useState(currentCollection.length);

  return (
    <GameSettingsContext.Provider
      value={{
        gameMode,
        setGameMode,
        rating,
        setRating,
        artists,
        setArtists,
        rounds,
        setRounds,
        showFavorites,
        setShowFavorites,
        difficulty,
        setDifficulty,
        isBlurry,
        setIsBlurry,
        blurAmount,
        setBlurAmount,
        hasTimeLimit,
        setHasTimeLimit,
        timeLimitMs,
        setTimeLimitMs,
        isCropped,
        setIsCropped,
        cropPercentage,
        setCropPercentage,
        isFlipped,
        setIsFlipped,
        isInverted,
        setIsInverted,
        isMosaic,
        setIsMosaic,
        isNoColor,
        setIsNoColor,
        isRanked,
        setIsRanked,
        artistRangeStart,
        setArtistRangeStart,
        artistRangeEnd,
        setArtistRangeEnd,
      }}
    >
      {children}
    </GameSettingsContext.Provider>
  );
};

export const useGameSettings = () => {
  const context = useContext(GameSettingsContext);
  if (context === undefined) {
    throw new Error('useGameSettings must be used within a GameSettingsProvider');
  }
  return context;
};

